(function () {
  var RESIZE_DEBOUNCE_MS = 250;
  var DESKTOP_BREAKPOINT = 960;
  var instances = new WeakMap();
  var resizeListenerBound = false;
  var resizeTimer = null;
  var isReinitializing = false;
  var lastInnerWidth = window.innerWidth;
  var breakpointMedia = window.matchMedia('(min-width: ' + DESKTOP_BREAKPOINT + 'px)');

  var prefersReducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  var gsapReady = function () {
    return window.gsap && window.ScrollTrigger;
  };

  var registerPlugins = function () {
    if (!gsapReady()) {
      return false;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);
    window.ScrollTrigger.config({ ignoreMobileResize: true });
    return true;
  };

  var getHeaderHeight = function () {
    return (
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0
    );
  };

  var getContentEdge = function (scrollContent) {
    if (!scrollContent) {
      return 0;
    }

    var styles = getComputedStyle(scrollContent);
    return parseFloat(styles.paddingLeft) || parseFloat(styles.paddingRight) || 0;
  };

  var getLastCardRight = function (scrollContent, lastCard) {
    return lastCard.offsetLeft + lastCard.offsetWidth;
  };

  var getScrollDistance = function (scrollTrack, scrollContent) {
    if (!scrollTrack || !scrollContent) {
      return 0;
    }

    var cards = scrollContent.querySelectorAll('.custom-horizontal-scroll__product-card');

    if (!cards.length) {
      return Math.max(0, scrollContent.scrollWidth - scrollTrack.offsetWidth);
    }

    var lastCard = cards[cards.length - 1];
    var trackWidth = scrollTrack.offsetWidth;
    var edge = getContentEdge(scrollContent);
    var innerRight = trackWidth - edge;
    var lastCardRight = getLastCardRight(scrollContent, lastCard);
    var distance = lastCardRight - innerRight;

    return Math.max(0, distance);
  };

  var resetScrollContent = function (scrollContent) {
    if (!scrollContent) {
      return;
    }

    window.gsap.set(scrollContent, { clearProps: 'transform' });
  };

  var applyPinOffset = function (scrollWrapper) {
    window.gsap.set(scrollWrapper, { top: getHeaderHeight() });
  };

  var clearPinOffset = function (scrollWrapper) {
    window.gsap.set(scrollWrapper, { clearProps: 'top' });
  };

  var refreshScrollTrigger = function () {
    if (window.ScrollTrigger) {
      window.ScrollTrigger.refresh();
    }
  };

  var updateViewportHeight = function () {
    document.documentElement.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
  };

  var syncPinOffset = function (instance) {
    if (instance && instance.scrollTrigger && instance.scrollTrigger.isActive && instance.scrollWrapper) {
      applyPinOffset(instance.scrollWrapper);
    }
  };

  var isSectionActive = function (section) {
    var instance = instances.get(section);
    return !!(instance && instance.scrollTrigger && instance.scrollTrigger.isActive);
  };

  var captureSectionStates = function () {
    var states = [];

    document.querySelectorAll('[data-custom-horizontal-scroll]').forEach(function (section) {
      var instance = instances.get(section);
      var progress = null;

      // Skip inactive sections: progress is 0 before the pin, which would jump here on resize.
      if (instance && instance.scrollTrigger && instance.scrollTrigger.isActive) {
        progress = instance.scrollTrigger.progress;
      }

      states.push({
        section: section,
        progress: progress,
      });
    });

    return states;
  };

  var restoreSectionStates = function (states) {
    states.forEach(function (state) {
      if (state.progress === null) {
        return;
      }

      var instance = instances.get(state.section);

      if (!instance || !instance.scrollTrigger) {
        return;
      }

      var scrollTrigger = instance.scrollTrigger;
      var range = scrollTrigger.end - scrollTrigger.start;

      if (range <= 0) {
        return;
      }

      window.scrollTo(0, scrollTrigger.start + range * state.progress);
    });
  };

  var restoreScrollY = function (scrollY) {
    window.scrollTo(0, scrollY);
  };

  var hasActiveProgress = function (states) {
    return states.some(function (state) {
      return state.progress !== null;
    });
  };

  var softRefreshAll = function () {
    if (isReinitializing) {
      return;
    }

    var savedScrollY = window.pageYOffset;
    var wasActive = false;

    document.querySelectorAll('[data-custom-horizontal-scroll]').forEach(function (section) {
      if (isSectionActive(section)) {
        wasActive = true;
      }

      syncPinOffset(instances.get(section));
    });

    refreshScrollTrigger();

    if (!wasActive) {
      restoreScrollY(savedScrollY);
    }
  };

  var reinitAllSections = function () {
    if (isReinitializing || prefersReducedMotion()) {
      if (prefersReducedMotion()) {
        document.querySelectorAll('[data-custom-horizontal-scroll]').forEach(destroySection);
      }

      return;
    }

    isReinitializing = true;

    var states = captureSectionStates();
    var savedScrollY = window.pageYOffset;
    var shouldRestoreProgress = hasActiveProgress(states);

    updateViewportHeight();

    document.querySelectorAll('[data-custom-horizontal-scroll]').forEach(function (section) {
      initHorizontalScroll(section);
    });

    window.requestAnimationFrame(function () {
      refreshScrollTrigger();

      if (shouldRestoreProgress) {
        restoreSectionStates(states);
        refreshScrollTrigger();
      } else {
        restoreScrollY(savedScrollY);
      }

      isReinitializing = false;
    });
  };

  var refreshSection = function (section) {
    if (prefersReducedMotion()) {
      destroySection(section);
      return;
    }

    if (!instances.get(section)) {
      initHorizontalScroll(section);
      refreshScrollTrigger();
      return;
    }

    softRefreshAll();
  };

  var scheduleReinit = function (force) {
    if (resizeTimer) {
      window.clearTimeout(resizeTimer);
    }

    resizeTimer = window.setTimeout(function () {
      resizeTimer = null;

      var widthChanged = window.innerWidth !== lastInnerWidth;
      lastInnerWidth = window.innerWidth;

      if (force || widthChanged) {
        reinitAllSections();
        return;
      }

      updateViewportHeight();
      softRefreshAll();
    }, RESIZE_DEBOUNCE_MS);
  };

  var bindGlobalResize = function () {
    if (resizeListenerBound) {
      return;
    }

    resizeListenerBound = true;
    window.addEventListener('resize', function () {
      scheduleReinit(false);
    });
    window.addEventListener('orientationchange', function () {
      scheduleReinit(true);
    });

    if (typeof breakpointMedia.addEventListener === 'function') {
      breakpointMedia.addEventListener('change', function () {
        scheduleReinit(true);
      });
    } else if (typeof breakpointMedia.addListener === 'function') {
      breakpointMedia.addListener(function () {
        scheduleReinit(true);
      });
    }
  };

  var destroySection = function (section) {
    var instance = instances.get(section);

    if (!instance) {
      return;
    }

    if (instance.onLayoutChange) {
      window.removeEventListener('header-height:change', instance.onLayoutChange);
    }

    if (instance.scrollTrigger) {
      instance.scrollTrigger.kill();
    }

    if (instance.tween) {
      instance.tween.kill();
    }

    if (instance.scrollWrapper) {
      clearPinOffset(instance.scrollWrapper);
    }

    resetScrollContent(instance.scrollContent);
    instances.delete(section);
  };

  var initHorizontalScroll = function (section) {
    destroySection(section);

    var scrollWrapper = section.querySelector('.scroll-wrapper');
    var scrollTrack = section.querySelector('.custom-horizontal-scroll__track');
    var scrollContent = section.querySelector('.scroll-content');

    if (!scrollWrapper || !scrollTrack || !scrollContent || prefersReducedMotion()) {
      return;
    }

    if (!registerPlugins()) {
      return;
    }

    var gsap = window.gsap;

    var scrollTween = gsap.to(scrollContent, {
      x: function () {
        return -getScrollDistance(scrollTrack, scrollContent);
      },
      ease: 'none',
      scrollTrigger: {
        trigger: scrollWrapper,
        pin: true,
        scrub: 1,
        start: function () {
          return 'top top+=' + getHeaderHeight();
        },
        end: function () {
          return '+=' + getScrollDistance(scrollTrack, scrollContent);
        },
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onEnter: function () {
          applyPinOffset(scrollWrapper);
        },
        onEnterBack: function () {
          applyPinOffset(scrollWrapper);
        },
        onRefresh: function (self) {
          if (self.isActive) {
            applyPinOffset(scrollWrapper);
          }
        },
        onLeave: function () {
          clearPinOffset(scrollWrapper);
        },
        onLeaveBack: function () {
          clearPinOffset(scrollWrapper);
        },
      },
    });

    var scrollTrigger = scrollTween.scrollTrigger;

    var onLayoutChange = function () {
      if (isReinitializing) {
        return;
      }

      syncPinOffset(instances.get(section));
      refreshScrollTrigger();
    };

    window.addEventListener('header-height:change', onLayoutChange);

    instances.set(section, {
      tween: scrollTween,
      scrollTrigger: scrollTrigger,
      scrollWrapper: scrollWrapper,
      scrollTrack: scrollTrack,
      scrollContent: scrollContent,
      onLayoutChange: onLayoutChange,
    });
  };

  var initSections = function (root) {
    var scope = root || document;

    scope.querySelectorAll('[data-custom-horizontal-scroll]').forEach(function (section) {
      initHorizontalScroll(section);
    });
  };

  var destroySections = function (root) {
    var scope = root || document;

    scope.querySelectorAll('[data-custom-horizontal-scroll]').forEach(function (section) {
      destroySection(section);
    });
  };

  var refreshAfterImages = function (section) {
    var scrollContent = section.querySelector('.scroll-content');

    if (!scrollContent) {
      return;
    }

    scrollContent.querySelectorAll('img').forEach(function (img) {
      if (img.complete) {
        return;
      }

      img.addEventListener(
        'load',
        function () {
          refreshSection(section);
        },
        { once: true }
      );
    });
  };

  var bootstrap = function () {
    bindGlobalResize();

    var start = function () {
      updateViewportHeight();
      initSections();
      refreshScrollTrigger();
    };

    if (gsapReady()) {
      start();
      return;
    }

    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;

      if (gsapReady()) {
        window.clearInterval(timer);
        start();
      } else if (attempts > 50) {
        window.clearInterval(timer);
      }
    }, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initSections(event.target);
    refreshAfterImages(event.target);
    refreshScrollTrigger();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroySections(event.target);
    refreshScrollTrigger();
  });
})();
